import React, { useEffect, useState } from "react";
import { Card, Upload, Button, Statistic, Row, Col, message, Popconfirm, Tag, Space, Modal, Form, Input, InputNumber, Collapse, Tooltip } from "antd";
import Alert from "../../components/GxAlert";
import {
  UploadOutlined, DeleteOutlined, ReloadOutlined, PlusOutlined, EditOutlined,
  CloudDownloadOutlined, QuestionCircleOutlined,
} from "@ant-design/icons";
import {
  scScaleUpload, scScaleList, scScaleClear, scScaleAdd, scScaleEdit,
  scScaleDeleteOne, scScaleFromSystem, scScaleBulkUpdate,
} from "../../api/client";
import { byStr, byNum } from "../../utils/sorters";
import EditableTable from "../../components/EditableTable";
import RowActions from "../../components/RowActions";
import { faNum } from "../../utils/faNumberFormat";

const faN = (n) => (n == null ? "—" : faNum(Number(n), { maximumFractionDigits: 3 }));
const SRC = {
  file: { l: "فایلِ پوشان", c: "blue" },
  system: { l: "دادهٔ سیستم", c: "purple" },
  manual: { l: "دستی", c: "gold" },
};

export default function ScaleDiscrepancyPage() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editRow, setEditRow] = useState(null);   // null=بسته، {}=افزودن، {id..}=ویرایش
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    scScaleList().then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onUpload = async (file) => {
    setUploading(true);
    try {
      const r = await scScaleUpload(file);
      message.success(`افزوده: ${r.data.added} • به‌روزرسانی: ${r.data.updated} • کلِ ردیف‌ها: ${r.data.total_rows}`);
      load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در آپلودِ کاردکسِ پوشان"); }
    setUploading(false);
    return false;
  };

  const fromSystem = async () => {
    try {
      const r = await scScaleFromSystem();
      message.success(`از سیستم افزوده: ${r.data.added} • تکراری: ${r.data.skipped}`);
      load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در خواندنِ دادهٔ سیستم"); }
  };

  const openAdd = () => setEditRow({});
  const openEdit = (row) => setEditRow(row);

  useEffect(() => {
    if (editRow == null) return;
    if (editRow.id) {
      form.setFieldsValue({
        receipt_number: editRow.receipt_number, jalali_date: editRow.jalali_date, roll_count: editRow.roll_count,
        company_kg: editRow.company_kg, pooshan_kg: editRow.pooshan_kg, fabric_name: editRow.fabric_name, note: editRow.note,
      });
    } else {
      form.resetFields();
    }
  }, [editRow, form]);
  const submit = async () => {
    const v = await form.validateFields();
    try {
      if (editRow && editRow.id) await scScaleEdit(editRow.id, v);
      else await scScaleAdd(v);
      message.success("ذخیره شد"); setEditRow(null); load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در ذخیره"); }
  };

  const delOne = async (id) => {
    try { await scScaleDeleteOne(id); message.success("حذف شد"); load(); }
    catch { message.error("خطا در حذف"); }
  };
  const onClear = async () => {
    try { await scScaleClear(); message.success("پاک شد"); load(); }
    catch { message.error("خطا در پاک‌سازی"); }
  };

  const s = data.summary || {};
  const kgRule = (v) => (v !== null && v !== undefined && Number(v) < 0 ? "وزن نمی‌تواند منفی باشد" : null);
  const cols = [
    { title: "شمارهٔ رسید", dataIndex: "receipt_number", sorter: byStr("receipt_number"), editType: "text", render: (v) => v || "—" },
    { title: "تاریخ", dataIndex: "jalali_date", sorter: byStr("jalali_date"), defaultSortOrder: "ascend", editType: "text", render: (v) => v || "—" },
    { title: "طاقه", dataIndex: "roll_count", align: "center", sorter: byNum("roll_count"), editType: "number", render: (v) => (v != null ? faN(v) : "—") },
    { title: "خروجِ بافندگی (کیلو)", dataIndex: "company_kg", align: "center", sorter: byNum("company_kg"), editType: "number", editRule: kgRule, render: (v) => (v != null ? faN(v) : "—") },
    { title: "ورودیِ پوشان (کیلو)", dataIndex: "pooshan_kg", align: "center", sorter: byNum("pooshan_kg"), editType: "number", editRule: kgRule, render: (v) => (v != null ? faN(v) : "—") },
    { title: "اختلاف (پوشان−بافنده)", dataIndex: "diff_kg", align: "center", sorter: byNum("diff_kg"),
      render: (v) => v == null
        ? <Tooltip title="هر دو وزن لازم است"><Tag>ناقص</Tag></Tooltip>
        : <b style={{ color: v > 0 ? "#cf1322" : v < 0 ? "#d46b08" : "#389e0d" }}>{v > 0 ? "+" : ""}{faN(v)}</b> },
    { title: "منبعِ خروجِ بافنده", dataIndex: "knitter_source", align: "center", sorter: byStr("knitter_source"),
      render: (v) => (SRC[v] ? <Tag color={SRC[v].c}>{SRC[v].l}</Tag> : "—") },
    { title: "شرح", dataIndex: "note", ellipsis: true, editType: "textarea", render: (v) => v || "—" },
    { title: "عملیات", key: "ops", align: "center", width: 110, fixed: "right", render: (_, r) => (
      <RowActions actions={[
        { key: "edit", label: "ویرایش", icon: <EditOutlined />, onClick: () => openEdit(r) },
        { key: "delete", label: "حذف", icon: <DeleteOutlined />, danger: true,
          confirm: "این ردیف حذف شود؟", onClick: () => delOne(r.id) },
      ]} />
    ) },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>اختلافِ باسکولی (خروجِ بافندگی ↔ ورودیِ پوشان)</h2>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="این بخش کاملاً مستقل است"
        description="مقایسهٔ وزنِ خروجیِ بافندگی (نوربخش/خوشباف) با وزنِ ورودیِ اعلامیِ پوشان. فقط برای دانستنِ مقدارِ اختلاف؛ در هیچ داشبورد، موجودی یا حساب‌وکتابی وارد نمی‌شود." />

      {/* آموزش */}
      <Collapse style={{ marginBottom: 12 }} items={[{
        key: "help",
        label: <Space><QuestionCircleOutlined /> راهنمای استفاده</Space>,
        children: (
          <div style={{ lineHeight: 2, fontSize: 13.5 }}>
            <b>اختلافِ باسکولی چیست؟</b> وقتی بافنده پارچه را به پوشان می‌فرستد، یک‌بار با باسکولِ شرکت وزن می‌شود
            («خروجِ بافندگی») و یک‌بار پوشان موقعِ تحویل وزن می‌کند («ورودیِ پوشان»). تفاوتِ این دو = اختلافِ باسکولی.
            <br />
            <b>سه راهِ پر کردنِ «خروجِ بافندگی»:</b>
            <ul style={{ margin: "4px 0" }}>
              <li><Tag color="blue">فایلِ پوشان</Tag> دکمهٔ «آپلودِ کاردکسِ پوشان» — هم ورودیِ پوشان و هم وزنِ باسکولِ شرکت را از خودِ فایل می‌خوانَد.</li>
              <li><Tag color="purple">دادهٔ سیستم</Tag> دکمهٔ «از دادهٔ سیستم» — خروجیِ بافندگی را از رویدادهای ثبت‌شده (ارسالِ بافنده → پوشان) می‌آورد.</li>
              <li><Tag color="gold">دستی</Tag> دکمهٔ «افزودنِ دستی» یا آیکنِ ویرایشِ هر ردیف — وزن‌ها را خودتان وارد/اصلاح کنید.</li>
            </ul>
            ستونِ <b>اختلاف</b> = ورودیِ پوشان − خروجِ بافندگی. مثبت (قرمز) یعنی پوشان بیشتر از باسکولِ شرکت ثبت کرده.
            ردیف‌هایی که فقط یک وزن دارند «ناقص»‌اند و در جمع نمی‌آیند. ردیف‌هایی که دستی ویرایش شوند، با آپلودِ دوبارهٔ فایل بازنویسی نمی‌شوند.
          </div>
        ),
      }]} />

      <Card style={{ marginBottom: 12 }}
        extra={<Space wrap>
          <Upload beforeUpload={onUpload} showUploadList={false} accept=".pdf,application/pdf">
            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>آپلودِ کاردکسِ پوشان (PDF)</Button>
          </Upload>
          <Button icon={<CloudDownloadOutlined />} onClick={fromSystem}>از دادهٔ سیستم</Button>
          <Button icon={<PlusOutlined />} onClick={openAdd}>افزودنِ دستی</Button>
          <Button icon={<ReloadOutlined />} onClick={load} />
          {data.rows.length > 0 && (
            <Popconfirm title="همهٔ ردیف‌های باسکولی پاک شوند؟" okText="پاک کن" cancelText="انصراف" onConfirm={onClear}>
              <Button danger icon={<DeleteOutlined />}>پاک‌سازیِ همه</Button>
            </Popconfirm>
          )}
        </Space>}>
        <Row gutter={[12, 12]}>
          <Col xs={12} md={6}><Statistic title="تعداد رسید" value={faN(s.count)} /></Col>
          <Col xs={12} md={6}><Statistic title="قابلِ مقایسه (هر دو وزن)" value={faN(s.comparable)} /></Col>
          <Col xs={12} md={6}><Statistic title="جمعِ خروجِ بافندگی (کیلو)" value={faN(s.total_company_kg)} /></Col>
          <Col xs={12} md={6}><Statistic title="جمعِ ورودیِ پوشان (کیلو)" value={faN(s.total_pooshan_kg)} /></Col>
        </Row>
        <Row gutter={[12, 12]} style={{ marginTop: 4 }}>
          <Col xs={12} md={6}><Statistic title="اختلافِ کل (کیلو)" value={faN(s.total_diff_kg)}
            valueStyle={{ color: (s.total_diff_kg || 0) > 0 ? "#cf1322" : (s.total_diff_kg || 0) < 0 ? "#d46b08" : "#389e0d" }} /></Col>
          <Col xs={12} md={6}><Statistic title="میانگینِ اختلاف" value={s.avg_diff_pct == null ? "—" : `${faN(s.avg_diff_pct)}٪`} /></Col>
        </Row>
      </Card>

      <Card>
        <EditableTable layoutId="sc-scale" dataSource={data.rows} columns={cols} rowKey="id" loading={loading}
          pagination={{ pageSize: 20 }}
          onSaveMany={async (items) => { await scScaleBulkUpdate(items); load(); }} />
      </Card>

      <Modal open={editRow != null} title={editRow && editRow.id ? "ویرایشِ ردیف" : "افزودنِ دستیِ ردیف"}
        onOk={submit} onCancel={() => setEditRow(null)} okText="ذخیره" cancelText="انصراف" destroyOnClose>
        <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
          <Row gutter={12}>
            <Col xs={12}><Form.Item name="receipt_number" label="شمارهٔ رسید/حواله"><Input /></Form.Item></Col>
            <Col xs={12}><Form.Item name="jalali_date" label="تاریخ (شمسی)"><Input placeholder="۱۴۰۵/۰۳/۰۲" /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12}><Form.Item name="company_kg" label="خروجِ بافندگی (کیلو)"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
            <Col xs={12}><Form.Item name="pooshan_kg" label="ورودیِ پوشان (کیلو)"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col xs={12}><Form.Item name="roll_count" label="طاقه"><InputNumber style={{ width: "100%" }} min={0} /></Form.Item></Col>
            <Col xs={12}><Form.Item name="fabric_name" label="نوع پارچه"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="note" label="شرح"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
